import React from "react";
import MuiTabs from "@mui/material/Tabs";
import { useTheme, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";

// Get tab indicator color from CSS variable defined in mui-styles.css
const getTabIndicatorColor = () => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue('--tab-indicator-color')
    .trim() || '#4b286d';
};

/**
 * Tabs component wrapper.
 * Works exactly the same as MUI Tabs with automatic wrapping on small screens.
 * 
 * @component
 * @example
 * <Tabs value={tabIndex} onChange={handleChange}>
 *   <Tab label="Profile" />
 *   <Tab label="Documents" />
 *   <Tab label="Users" />
 * </Tabs>
 */
const Tabs = ({ sx, indicatorColor = "secondary", textColor = "primary", ...props }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const wrappingStyles = isSmallScreen
    ? {
        "& .MuiTabs-flexContainer": {
          flexWrap: "wrap",
        },
        "& .MuiTabs-indicator": {
          display: "none",
        },
        "& .MuiTab-root": {
          borderBottom: "3px solid transparent",
          transition: "border-color 0.3s",
        },
        "& .MuiTab-root.Mui-selected": {
          borderBottomColor: getTabIndicatorColor(),
        },
      }
    : {};

  const mergedSx = {
    ...wrappingStyles,
    ...(typeof sx === "function" ? sx(theme) : sx),
  };

  return <MuiTabs sx={mergedSx} indicatorColor={indicatorColor} {...props} />;
};

Tabs.propTypes = {
  children: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(["standard", "scrollable", "fullWidth"]),
  scrollButtons: PropTypes.oneOf(["auto", true, false]),
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  allowScrollButtonsMobile: PropTypes.bool,
  indicatorColor: PropTypes.oneOf(["primary", "secondary"]),
  textColor: PropTypes.oneOf(["inherit", "primary", "secondary"]),
  centered: PropTypes.bool,
  component: PropTypes.elementType,
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
  "aria-label": PropTypes.string,
  "aria-labelledby": PropTypes.string,
};

export default Tabs;

